import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Select } from 'shared/ui/Select/Select'
import {
  Button,
  EnumDropdownItemVariant,
  type IDropdownItem,
  type IStatusProps,
  Status,
} from 'shared/ui'
import type { IFilterValue } from 'entities/Filters/types'
import type { IWidgetProps } from 'widgets/FilterEditor/types/types'

import { WidgetStatusTrigger } from './WidgetStatusTrigger'
import styles from './styles.module.scss'

type IWidgetStatusProps<T extends string = string> = {
  getConfigByStatus: (status: T) => IStatusProps
} & IWidgetProps<IFilterValue<T>, T[]>

export const WidgetStatus = observer(
  <T extends string = string>({
    filterItem,
    expandByDefault = false,
    parentsDropdownId,
    getConfigByStatus,
  }: IWidgetStatusProps<T>) => {
    const [showDropdown, setShowDropdown] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterConfig = filterItem.config
    const values = filterConfig.values ?? []
    const payloadValue = useMemo(
      () => (Array.isArray(filterItem.payload.value) ? filterItem.payload.value : []),
      [filterItem.payload.value]
    )
    const [primaryKeys, setPrimaryKeys] = useState<T[]>([])
    const [selectedKeys, setSelectedKeys] = useState<T[]>(payloadValue)
    const dropdownItems: IDropdownItem[] = useMemo(
      () =>
        values
          .reduce<[IFilterValue<T>[], IFilterValue<T>[]]>(
            (values, value) => {
              const isSelected = primaryKeys.includes(value.key)

              if (isSelected) values[0].push(value)
              else values[1].push(value)

              return values
            },
            [[], []]
          )
          .flat()
          .map((item, index) => {
            const key = item.key

            const statusConfig = getConfigByStatus(key)

            return {
              id: key,
              label: statusConfig?.title || item.label,
              labelContent: statusConfig ? <Status {...statusConfig} /> : item.label,
              labelCount: item.counter,
              variant: EnumDropdownItemVariant.Checkbox,
              activeValue: selectedKeys.includes(key),
              divider: index === primaryKeys.length - 1 && primaryKeys.length !== values.length,
            }
          }),
      [selectedKeys, primaryKeys]
    )

    useEffect(() => {
      setPrimaryKeys(payloadValue)
    }, [])

    useEffect(() => {
      setTimeout(() => setShowDropdown(expandByDefault), 100)
    }, [expandByDefault])

    return (
      <Select
        text={filterItem.operator?.key ?? ''}
        type='multi'
        items={dropdownItems}
        dropdownProps={{
          ariaLabel: 'Select',
          parentsDropdownId: parentsDropdownId,
          show: showDropdown,
          textFieldProps: {
            InputProps: { placeholder: `Search ${filterItem.config.name}` },
          },
          bottomActions: selectedKeys.length
            ? () => (
                <Button
                  text='Clear'
                  contained='secondary'
                  typeBtn='text'
                  onClick={() => {
                    setPrimaryKeys([])
                    setSelectedKeys([])
                    filterItem.payload.updateHard({ value: [] })
                  }}
                />
              )
            : undefined,
          withSearch: true,
          onClose: () => {
            setPrimaryKeys(selectedKeys)
            setShowValuesWarning(!selectedKeys.length)
          },
          triggerComponent: () => (
            <div className={classNames({ [styles.warning]: showValuesWarning })}>
              <WidgetStatusTrigger items={selectedKeys} getConfigByStatus={getConfigByStatus} />
            </div>
          ),
          onChange: (item) => {
            const keys = (
              item.activeValue
                ? selectedKeys.filter((key) => key !== item.id)
                : [...selectedKeys, item.id]
            ) as T[]

            setShowValuesWarning(false)
            setSelectedKeys(keys)
            filterItem.payload.updateHard({ value: keys })
          },
        }}
      />
    )
  }
)
